import React from 'react';

import { Text } from '@thefarmersfront/kitchen';

import type { ICellRendererParams } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';

import styles from './MultiRowCell.module.scss';

type ItemRendererFunction<TItem> = (item: TItem, index: number) => React.ReactNode;

type ItemRendererComponent<TItem> = React.ComponentType<{
  value: unknown;
  item: TItem;
  index: number;
  [key: string]: unknown;
}>;

type TypeMultiRowCellParams<TValue extends Record<string, unknown>[]> = {
  /** 표기할 필드명 */
  field: string;
  /** 개별 아이템 렌더러 */
  itemRenderer?: ItemRendererFunction<TValue[number]> | ItemRendererComponent<TValue[number]>;
  /** 개별 아이템 렌더러 파라미터 */
  itemRendererParams?:
    | Record<string, unknown>
    | ((params: ICellRendererParams<TValue[number]>) => Record<string, unknown>);
  /** 셀 스타일 */
  cellStyle?: React.CSSProperties;
  /**
   * 값이 없을 경우 대시(-)로 표시할지 여부
   * @default true
   */
  showDashIfNoValue?: boolean;
};

type TypeMultiRowCellProps<TData, TValue extends Record<string, unknown>[]> = TypeMultiRowCellParams<TValue> &
  CustomCellRendererProps<TData, TValue>;

// 아이템 렌더러가 함수인지 컴포넌트인지 확인하는 타입 가드
const isItemRendererFunction = <TItem,>(
  renderer: ItemRendererFunction<TItem> | ItemRendererComponent<TItem>
): renderer is ItemRendererFunction<TItem> => {
  return typeof renderer === 'function' && renderer.length >= 2;
};

const createDefaultItemRenderer = <TItem extends Record<string, unknown>>(
  item: TItem,
  field: string
): React.ReactNode => {
  return (
    <Text className={styles['multi-row-value']} size="large">
      {String(item?.[field] ?? '-')}
    </Text>
  );
};

const MultiRowCell = <TData, TValue extends Record<string, unknown>[]>({
  node,
  value,
  field,
  itemRenderer,
  itemRendererParams,
  cellStyle,
  showDashIfNoValue = true,
}: TypeMultiRowCellProps<TData, TValue>) => {
  if (!value || value.length === 0) {
    if (showDashIfNoValue) {
      return (
        <div className={styles['multi-row-cell']} style={cellStyle}>
          <Text className={styles['multi-row-value']} size="large">
            -
          </Text>
        </div>
      );
    }

    // showDashIfNoValue가 false이고 itemRenderer가 있는 경우 itemRenderer 사용
    if (itemRenderer) {
      let content: React.ReactNode;

      try {
        // 빈 데이터를 위한 기본 객체 생성
        const emptyItem = { [field]: null } as TValue[number];

        if (isItemRendererFunction(itemRenderer)) {
          content = itemRenderer(emptyItem, 0);
        } else {
          const rendererProps = {
            data: emptyItem,
            value: null,
            item: emptyItem,
            index: 0,
            ...(typeof itemRendererParams === 'function'
              ? itemRendererParams({
                  node,
                  data: emptyItem,
                  value: null,
                } as ICellRendererParams)
              : itemRendererParams || {}),
          };

          content = React.createElement(itemRenderer, rendererProps);
        }
      } catch (error) {
        content = (
          <Text className={styles['multi-row-value']} size="large">
            -
          </Text>
        );
      }

      return (
        <div className={styles['multi-row-cell']} style={cellStyle}>
          {content}
        </div>
      );
    }

    // 아무것도 없으면 빈 div 반환
    return <div className={styles['multi-row-cell']} style={cellStyle}></div>;
  }

  return (
    <>
      {value?.map((item, index) => {
        const keyOfCell = `multi-row-cell-${index}`;

        let content: React.ReactNode;

        if (!itemRenderer) {
          content = createDefaultItemRenderer(item, field);
        } else if (isItemRendererFunction(itemRenderer)) {
          content = itemRenderer(item, index);
        } else {
          const rendererProps = {
            data: item,
            value: item[field],
            item,
            index,
            ...(typeof itemRendererParams === 'function'
              ? itemRendererParams({
                  node,
                  data: item,
                  value: item[field],
                } as ICellRendererParams)
              : itemRendererParams || {}),
          };

          content = React.createElement(itemRenderer, rendererProps);
        }

        return (
          <div key={keyOfCell} className={styles['multi-row-cell']} style={cellStyle}>
            {content}
          </div>
        );
      })}
    </>
  );
};

export default MultiRowCell;
